import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { pitchEventAPI, adminAPI } from '../lib/api';
import { getApiUrl } from '../config/api.config';

const EventOrganizerDashboard = () => {
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const { user } = useAuth();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedEvent, setExpandedEvent] = useState(null);
    const [eventParticipants, setEventParticipants] = useState({});
    const [eventInvestors, setEventInvestors] = useState({});
    const [loadingParticipants, setLoadingParticipants] = useState({});
    const [stats, setStats] = useState({
        totalEvents: 0,
        upcomingEvents: 0,
        totalApplications: 0,
        activeParticipants: 0
    });

    useEffect(() => {
        loadOrganizerEvents();
    }, []);

    const loadOrganizerEvents = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(getApiUrl('pitch-events/organizer/my-events'), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setEvents(data);

            // Calculate stats - need to load participants for accurate count
            const upcoming = data.filter(e => e.eventStatus === 'UPCOMING').length;
            
            // Load participants for all events to get accurate counts
            let totalParticipants = 0;
            for (const event of data) {
                try {
                    const participantsResponse = await fetch(getApiUrl(`pitch-events/${event.id}/participants`), {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (participantsResponse.ok) {
                        const participants = await participantsResponse.json();
                        totalParticipants += participants.length;
                    }
                } catch (err) {
                    console.error(`Failed to load participants for event ${event.id}:`, err);
                }
            }

            setStats({
                totalEvents: data.length,
                upcomingEvents: upcoming,
                totalApplications: totalParticipants,
                activeParticipants: totalParticipants
            });
        } catch (error) {
            console.error('Failed to load events:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadEventParticipants = async (eventId) => {
        if (eventParticipants[eventId]) {
            return; // Already loaded
        }

        setLoadingParticipants({ ...loadingParticipants, [eventId]: true });
        try {
            const participants = await pitchEventAPI.getEventParticipants(eventId);
            setEventParticipants({ ...eventParticipants, [eventId]: participants });

            // Load investors for participating startups
            const startupIds = participants.map(p => p.startup?.id || p.startupId).filter(Boolean);
            if (startupIds.length > 0) {
                await loadInvestorsForStartups(eventId, startupIds);
            }
        } catch (error) {
            console.error(`Failed to load participants for event ${eventId}:`, error);
        } finally {
            setLoadingParticipants({ ...loadingParticipants, [eventId]: false });
        }
    };

    const loadInvestorsForStartups = async (eventId, startupIds) => {
        try {
            const token = localStorage.getItem('token');
            const investorsSet = new Set();
            
            // For each startup, get their investors through investments
            for (const startupId of startupIds) {
                try {
                    const investmentsResponse = await fetch(getApiUrl('investments'), {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (investmentsResponse.ok) {
                        const investments = await investmentsResponse.json();
                        const startupInvestments = investments.filter(inv => 
                            inv.startup?.id === startupId || inv.startupId === startupId
                        );
                        startupInvestments.forEach(inv => {
                            if (inv.investor?.id) {
                                investorsSet.add(JSON.stringify({
                                    id: inv.investor.id,
                                    name: inv.investor.name,
                                    email: inv.investor.email,
                                    organizationName: inv.investor.organizationName
                                }));
                            }
                        });
                    }
                } catch (err) {
                    console.error(`Failed to load investments for startup ${startupId}:`, err);
                }
            }

            const investorsArray = Array.from(investorsSet).map(JSON.parse);
            setEventInvestors({ ...eventInvestors, [eventId]: investorsArray });
        } catch (error) {
            console.error(`Failed to load investors for event ${eventId}:`, error);
        }
    };

    const toggleEventDetails = (eventId) => {
        if (expandedEvent === eventId) {
            setExpandedEvent(null);
        } else {
            setExpandedEvent(eventId);
            loadEventParticipants(eventId);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'UPCOMING': return 'bg-blue-100 text-blue-800';
            case 'ONGOING': return 'bg-green-100 text-green-800';
            case 'COMPLETED': return 'bg-gray-100 text-gray-800';
            case 'CANCELLED': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-20">
                {/* Welcome Section */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Welcome, {user?.name}</h1>
                    <p className="text-gray-600 mt-2">Manage your pitch events and review startup applications</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Total Events</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.totalEvents}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Upcoming Events</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.upcomingEvents}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Total Applications</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                </div>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Active Participants</p>
                                <p className="text-2xl font-bold text-gray-900">{stats.activeParticipants}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Events List */}
                <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">My Events</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Participants</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {events.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <h3 className="mt-2 text-sm font-medium text-gray-900">No events yet</h3>
                                            <p className="mt-1 text-sm text-gray-500">Contact admin to get events assigned to you.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    events.map((event) => (
                                        <React.Fragment key={event.id}>
                                            <tr className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <button
                                                            onClick={() => toggleEventDetails(event.id)}
                                                            className="mr-2 text-gray-400 hover:text-gray-600"
                                                        >
                                                            <svg 
                                                                className={`w-5 h-5 transform transition-transform ${expandedEvent === event.id ? 'rotate-90' : ''}`}
                                                                fill="none" 
                                                                stroke="currentColor" 
                                                                viewBox="0 0 24 24"
                                                            >
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </button>
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">{event.eventName}</div>
                                                            <div className="text-sm text-gray-500">{event.eventType}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {new Date(event.eventDate).toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {event.location || 'TBD'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {eventParticipants[event.id]?.length || 0} / {event.maxParticipants || '∞'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(event.eventStatus)}`}>
                                                        {event.eventStatus}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <button
                                                        onClick={() => navigate(`/${tenantId}/events/${event.id}`)}
                                                        className="text-blue-600 hover:text-blue-900 mr-4"
                                                    >
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                            {expandedEvent === event.id && (
                                                <tr>
                                                    <td colSpan="6" className="px-6 py-4 bg-gray-50">
                                                        {loadingParticipants[event.id] ? (
                                                            <div className="flex items-center justify-center py-4">
                                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                                            </div>
                                                        ) : (
                                                            <div className="space-y-6">
                                                                {/* Startups Section */}
                                                                <div>
                                                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Participating Startups</h3>
                                                                    {eventParticipants[event.id]?.length > 0 ? (
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                                            {eventParticipants[event.id].map((participant) => (
                                                                                <div key={participant.id} className="bg-white rounded-lg shadow p-4 border border-gray-200">
                                                                                    <div className="flex items-center space-x-3">
                                                                                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center">
                                                                                            <span className="text-white font-medium">
                                                                                                {(participant.startup?.name || participant.startup?.organizationName || 'S')?.charAt(0)?.toUpperCase()}
                                                                                            </span>
                                                                                        </div>
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                                                                {participant.startup?.organizationName || participant.startup?.name || 'Unknown Startup'}
                                                                                            </p>
                                                                                            <p className="text-xs text-gray-500 truncate">
                                                                                                {participant.startup?.email || 'No email'}
                                                                                            </p>
                                                                                            <p className="text-xs text-blue-600 mt-1">
                                                                                                Stage: {participant.stage || 'N/A'}
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <p className="text-sm text-gray-500">No startups participating yet</p>
                                                                    )}
                                                                </div>

                                                                {/* Investors Section */}
                                                                <div>
                                                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Related Investors</h3>
                                                                    {eventInvestors[event.id]?.length > 0 ? (
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                                            {eventInvestors[event.id].map((investor, index) => (
                                                                                <div key={investor.id || index} className="bg-white rounded-lg shadow p-4 border border-gray-200">
                                                                                    <div className="flex items-center space-x-3">
                                                                                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 flex items-center justify-center">
                                                                                            <span className="text-white font-medium">
                                                                                                {(investor.name || investor.organizationName || 'I')?.charAt(0)?.toUpperCase()}
                                                                                            </span>
                                                                                        </div>
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                                                                {investor.organizationName || investor.name || 'Unknown Investor'}
                                                                                            </p>
                                                                                            <p className="text-xs text-gray-500 truncate">
                                                                                                {investor.email || 'No email'}
                                                                                            </p>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    ) : (
                                                                        <p className="text-sm text-gray-500">No investors linked to participating startups</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventOrganizerDashboard;
